import type {
  CustomerDomain,
  CustomerSettings,
} from "@/redux/reducer/customers.api";
import {
  ontameJobIdTrackEventHTML,
  ontameScriptHTML,
} from "../../../shared/ontameScriptHTML";
import CONSENT_CORE from "../../../shared/consentCore.js?raw";

const YOUTUBE_EMBED_URL = /\/\/(?:www\.)?youtube\.com\/embed\//g;
const YOUTUBE_NOCOOKIE_EMBED_URL = "//www.youtube-nocookie.com/embed/";

const OUTSIDE_FRAME = "window.self === window.top";

export const consentCoreHTML = (embeddedInIframe: boolean) =>
  `<script>(${CONSENT_CORE.trim()})(window, ${embeddedInIframe});</script>`;

// Adds a query parameter to a URL taken from an HTML attribute, before any #fragment, unless it is there.
const withParameter = (spacedUrl: string, name: string, value: string) => {
  const url = spacedUrl.trim();
  const hashAt = url.indexOf("#");
  const base = hashAt < 0 ? url : url.slice(0, hashAt);
  const hash = hashAt < 0 ? "" : url.slice(hashAt);
  if (new RegExp(`[?&](?:amp;)?${name}=`).test(base)) {
    return url;
  }
  return `${base}${base.includes("?") ? "&amp;" : "?"}${name}=${value}${hash}`;
};

// ReachMee reads notrack from the form URL; whatever value the page had, the saved form loads without tracking.
const withNoTracking = (spacedUrl: string) => {
  const url = spacedUrl.trim();
  const hashAt = url.indexOf("#");
  const base = (hashAt < 0 ? url : url.slice(0, hashAt))
    .replace(/([?&](?:amp;)?)notrack(?:=[^&#]*)?(?:&(?:amp;)?)?/gi, "$1")
    .replace(/[?&](?:amp;)?$/, "");
  const hash = hashAt < 0 ? "" : url.slice(hashAt);
  return `${base}${base.includes("?") ? "&amp;" : "?"}notrack=1${hash}`;
};

// A URL runs to the attribute's own closing quote, so an apostrophe inside double quotes stays part of it.
// Only ReachMee: notrack is its adapter, Talent Recruiter forms run their own consent inside the frame.
const REACHMEE_FORM_IFRAME =
  /<iframe\b([^>]*?)\ssrc=(["'])\s*((?:https?:)?\/\/(?:[a-z0-9-]+\.)*reachmee\.com\/(?:(?!\2)[^>])*?)\s*\2/gi;
const APPLY_URL_IFRAME = /<iframe\b([^>]*?)\ssrc=(["'])\s*\{APPLY_URL\}\s*\2/gi;
const DATA_URL_ATTRIBUTE = /\sdata-url=(["'])[^"']*\1/i;

// A form saved into the page loads without tracking; the consent script turns tracking on after consent.
const markAtsForms = (html: string) =>
  html
    .replace(APPLY_URL_IFRAME, (start, before: string, quote: string) =>
      /\sdata-tt-ats-form\b/.test(before)
        ? start
        : `<iframe data-tt-ats-form${before.replace(DATA_URL_ATTRIBUTE, "")} data-url=${quote}{APPLY_URL}${quote}`
    )
    .replace(
      REACHMEE_FORM_IFRAME,
      (start, before: string, quote: string, url: string) =>
        /\sdata-tt-ats-form\b/.test(before)
          ? start
          : `<iframe data-tt-ats-form${before} src=${quote}${withNoTracking(url)}${quote}`
    );

const VIMEO_IFRAME_SRC =
  /(<iframe\b[^>]*?\ssrc=)(["'])\s*((?:https?:)?\/\/player\.vimeo\.com\/video\/(?:(?!\2)[^>])*?)\s*\2/gi;

// Vimeo's do-not-track mode sets no cookies; a video's own dnt value is kept.
const withVimeoDoNotTrack = (html: string) =>
  html.replace(
    VIMEO_IFRAME_SRC,
    (_, start: string, quote: string, url: string) =>
      `${start}${quote}${withParameter(url, "dnt", "1")}${quote}`
  );

// The standard job ad script from before ADR-0007 built its form iframe with the tracking URL.
const OLD_STANDARD_JOB_AD_APPLY = "ifr.src = u; ifr.title = ";
const STANDARD_JOB_AD_APPLY =
  'ifr.setAttribute("data-tt-ats-form", ""); ifr.src = window.talentechConsent.formUrl(u); ifr.title = ';

const SCRIPT_ELEMENT = /^(<script[^>]*>)([\s\S]*)(<\/script>)$/;

export const outsideFrameOnly = (scriptHTML: string) =>
  scriptHTML.replace(
    SCRIPT_ELEMENT,
    (_, open: string, code: string, close: string) =>
      `${open}if (${OUTSIDE_FRAME}) {${code}}${close}`
  );

const cookiebotTagHTML = (cid: string) =>
  `<script id="Cookiebot" src="https://consent.cookiebot.com/uc.js" data-cbid="${cid}" type="text/javascript"></script>`;

// document.write keeps Cookiebot parser-blocking, as the plain tag is, so consent events keep their timing.
const cookiebotOutsideFrameHTML = (cid: string) =>
  `<script>if (${OUTSIDE_FRAME}) document.write(${JSON.stringify(
    cookiebotTagHTML(cid)
  ).replaceAll("</", "<\\/")});</script>`;

const holdsPage = (domain: CustomerDomain, pageId: string) =>
  domain.joblist_page?.joblist_page_id === pageId ||
  domain.joblist_page?.job_ad_pages
    .map((page) => page.job_ad_page_id)
    .includes(pageId);

export const findPageDomain = (
  domains: CustomerDomain[] | undefined,
  pageId: string
): CustomerDomain | undefined =>
  domains?.find((domain) => holdsPage(domain, pageId));

// A job ad page can be under several domains, listed in no fixed order; one embedded domain is enough.
export const isEmbeddedInIframe = (
  domains: CustomerDomain[] | undefined,
  pageId: string
) =>
  domains?.some(
    (domain) => domain.embedded_in_iframe === true && holdsPage(domain, pageId)
  ) ?? false;

export const cookiebotHeadHTML = ({
  settings,
  embeddedInIframe,
}: {
  settings: CustomerSettings | undefined;
  embeddedInIframe: boolean;
}) => {
  const cookiebot = settings?.privacy.cookies.cookiebot;
  if (!cookiebot?.cid || !cookiebot.enable) {
    return "";
  }
  return embeddedInIframe
    ? cookiebotOutsideFrameHTML(cookiebot.cid)
    : cookiebotTagHTML(cookiebot.cid);
};

export const ontameHeadHTML = ({
  settings,
  tenantId,
  isJobPage,
  embeddedInIframe,
}: {
  settings: CustomerSettings | undefined;
  tenantId: string;
  isJobPage: boolean;
  embeddedInIframe: boolean;
}): string[] => {
  if (!settings?.privacy.analytics.ontame) {
    return [];
  }
  const scripts = isJobPage
    ? [ontameScriptHTML(tenantId), ontameJobIdTrackEventHTML()]
    : [ontameScriptHTML(tenantId)];
  return embeddedInIframe ? scripts.map(outsideFrameOnly) : scripts;
};

export const pageBodyHTML = ({
  htmlSrc,
  jsSrc,
}: {
  htmlSrc: string;
  jsSrc: string | undefined;
}) => {
  const contentHTML = [
    markAtsForms(
      withVimeoDoNotTrack(
        htmlSrc.replaceAll(YOUTUBE_EMBED_URL, YOUTUBE_NOCOOKIE_EMBED_URL)
      )
    ),
  ];

  if (jsSrc) {
    contentHTML.push(
      `<script>${jsSrc.replaceAll(OLD_STANDARD_JOB_AD_APPLY, STANDARD_JOB_AD_APPLY)}</script>`
    );
  }

  return contentHTML.join("");
};
